import {
  Account,
  FeatureName,
  PermissionValues as Permissions,
  WorkspaceType
} from '@gripp/shared-logic';
import {
  IconName
} from '@gripp/shared-ui';
import { FC } from 'react';
import { AssetMenuItem } from '../../assets/components/assetsMenuItem';
import { NavLinkProps } from './menuLink';

export interface IMenu {
  titleKey: string;
  icon: IconName | string;
  route: string;
  requiredPermission: string;
  requiredFeature?: FeatureName;
  requiredWorkspaceType?: WorkspaceType;
  isWorkspaceRequired?: boolean | ((account: Account | null) => boolean);
  component?: FC<NavLinkProps>;
}

export enum Routes {
  AccountPage = 'account-page',
  Accounts = 'accounts',
  Activities = 'activities',
  Assets = 'assets',
  Groups = 'groups',
  Routines = 'routines',
  NewRoutines = 'new-routines',
  Tags = 'tags',
  TagBatches = 'tagbatches',
  Workspaces = 'workspaces',
  Features = 'features',
  AlertsManagement = 'alerts-management',
  Products = 'products',
  Files = 'files',
  AddOns = 'add-ons',
}

export const routeNames = {
  [Routes.AccountPage]: `/account-page`,
  [Routes.Accounts]: `/accounts`,
  [Routes.Activities]: `/activities`,
  [Routes.Assets]: `/assets`,
  [Routes.Groups]: `/groups`,
  [Routes.Routines]: `/routines`,
  [Routes.NewRoutines]: `/new-routines`,
  [Routes.Products]: `/products`,
  [Routes.Files]: `/files`,
  [Routes.Tags]: `/tags`,
  [Routes.TagBatches]: `/tagbatches`,
  [Routes.Workspaces]: `/workspaces`,
  [Routes.Features]: `/features`,
  [Routes.AlertsManagement]: `/alerts-management`,
  [Routes.AddOns]: `/add-ons`,
};

const guestMenuItems: IMenu[] = [
  {
    titleKey: 'activity.menu.title',
    icon: 'Activity',
    route: routeNames[Routes.Activities],
    requiredPermission: Permissions.activity.read,
  },
  {
    titleKey: 'assets.menu.title',
    icon: 'QR',
    route: routeNames[Routes.Assets],
    requiredPermission: Permissions.asset.read,
    component: AssetMenuItem,
  },
  {
    titleKey: 'admin.menu.admin',
    icon: 'People',
    route: routeNames[Routes.Accounts],
    requiredPermission: Permissions.peoplepage.read,
    isWorkspaceRequired: (account: Account | null) => !account?.isGlobalAdmin(),
  },
];

const adminMenuItems: IMenu[] = [
  {
    titleKey: 'routine.list.oldRoutines',
    icon: 'Remove',
    route: routeNames[Routes.Routines],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'routine.list.routines',
    icon: 'Routine',
    route: routeNames[Routes.NewRoutines],
    requiredPermission: Permissions.routinespage.read,
    requiredFeature: FeatureName.NewRoutinesPage,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'tags.title',
    icon: 'Tag',
    route: routeNames[Routes.Tags],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'tags.batches.title',
    icon: 'Remove',
    route: routeNames[Routes.TagBatches],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'features.title',
    icon: 'Remove',
    route: routeNames[Routes.Features],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'alertsManagement.menu.title',
    icon: 'Alert',
    route: routeNames[Routes.AlertsManagement],
    requiredPermission: Permissions.globalAdmin,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'admin.menu.workspaces',
    icon: 'Remove',
    route: routeNames[Routes.Workspaces],
    requiredPermission: Permissions.globalAdmin,
  },
];

const primaryAdminMenuItems: IMenu[] = [
  {
    titleKey: 'groups.menu.title',
    icon: 'Groups',
    route: routeNames[Routes.Groups],
    requiredPermission: Permissions.grouppage.read,
    requiredFeature: FeatureName.GroupsPage,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'product.list.products',
    icon: 'Tag',
    route: routeNames[Routes.Products],
    requiredPermission: Permissions.product.all,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'addOn.list.addOns',
    icon: 'Addons',
    route: routeNames[Routes.AddOns],
    requiredPermission: Permissions.addon.all,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'file.list.files',
    icon: 'Document',
    route: routeNames[Routes.Files],
    requiredPermission: Permissions.filespage.read,
    requiredFeature: FeatureName.FilesPage,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
];

const supportMenuItems: IMenu[] = [
  {
    titleKey: 'accountPage.menu.accountPage',
    icon: 'Settings',
    route: routeNames[Routes.AccountPage],
    requiredPermission: Permissions.account.allWorkspace,
    requiredFeature: FeatureName.AccountPage,
  },
];

export const MenuItems: IMenu[][] = [
  primaryAdminMenuItems,
  guestMenuItems,
  adminMenuItems,
  supportMenuItems,
];
